// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import styles from './CoachingLines.module.css'
import type { OwnerCoaching, OwnerCoachingLine } from 'lib/dealhunt/types'

interface CoachingLinesProps {
  /** `verdict.coaching` at publish, or `shelf[].note.coaching` on /me. undefined = null (§3). */
  coaching?: OwnerCoaching | null
  /** Which of the two independent slots this surface owns. The band shows both; the own-note
   *  card shows `clue` alone (the author read the recognition at publish); the return card
   *  shows `recognition` alone. */
  show?: 'both' | 'recognition' | 'clue'
  className?: string
}

/** A slot is renderable only with a non-empty sentence — a blank `text` is nothing to say. */
function lineOf(line: OwnerCoachingLine | null | undefined): string | null {
  const text = line?.text?.trim()
  return text ? text : null
}

/**
 * The author's coaching sentences (DealHunt ledger #144/#149), rendered VERBATIM as plain text:
 * every sentence is server-written from a closed frame table (#48), so there is nothing to
 * sanitise, nothing to re-word, and nothing of ours to append.
 *
 * Two INDEPENDENT slots — either may be null on its own. `prompt_id` is an analytics id and is
 * never rendered (it reaches GA4 through the calling surface's event, never the DOM).
 *
 * The empty state is the DEFAULT, not an error: the peer line needs a second published note on
 * the same product, so today it fires for nobody. With nothing to say this renders NOTHING — no
 * wrapper, no margin — so every host must look finished without it.
 */
const CoachingLines: React.VFC<CoachingLinesProps> = ({ coaching, show = 'both', className }) => {
  const recognition = show === 'clue' ? null : lineOf(coaching?.recognition)
  const clue = show === 'recognition' ? null : lineOf(coaching?.clue)
  if (!recognition && !clue) {
    return null
  }
  return (
    <div className={clsx(styles.root, className)}>
      {recognition && <p className={styles.recognition}>{recognition}</p>}
      {clue && <p className={styles.clue}>{clue}</p>}
    </div>
  )
}

export default CoachingLines
